import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { useDebouncedCallback } from 'use-debounce';
import { Order, OrderStatus } from '../types';
import { OrderSortField, OrderSortOrder } from '../components/OrderSort';
import { searchOrders } from '../services/orderService';

interface UseCustomerOrdersOptions {
  customerId: string;
  pageSize?: number;
  enabled?: boolean;
}

interface CustomerOrdersResult {
  orders: Order[];
  total: number;
}

interface UseCustomerOrdersResult {
  orders: Order[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  searchQuery: string;
  selectedStatuses: OrderStatus[];
  selectedPaymentStatuses: string[];
  sortField: OrderSortField | null;
  sortOrder: OrderSortOrder;
  isSearchMode: boolean;
  isFilterMode: boolean;
  isSortMode: boolean;
  isCombinedMode: boolean;
  searchLoading: boolean;
  filterLoading: boolean;
  sortLoading: boolean;
  summaryStats: {
    totalOrders: number;
    totalSpent: number;
    averageOrderValue: number;
  };
  setSearchQuery: (query: string) => void;
  setSelectedStatuses: (statuses: OrderStatus[]) => void;
  setSelectedPaymentStatuses: (statuses: string[]) => void;
  setSorting: (field: OrderSortField | null, order: OrderSortOrder) => void;
  handleSearch: (query: string) => void;
  fetchPage: (page: number) => void;
  refresh: () => Promise<void>;
  reset: () => void;
}

/**
 * Hook for fetching customer orders with search, filtering, sorting, and pagination
 * Follows the same patterns as usePaginatedOrders but specifically for customer orders
 */
export const useCustomerOrders = ({ 
  customerId,
  pageSize = 10,
  enabled = true 
}: UseCustomerOrdersOptions): UseCustomerOrdersResult => {
  // State management
  const [allCustomerOrders, setAllCustomerOrders] = useState<Order[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [summaryStats, setSummaryStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    averageOrderValue: 0,
  });
  
  // Search state
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<Order[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Filter state
  const [isFilterMode, setIsFilterMode] = useState(false);
  const [filterResults, setFilterResults] = useState<Order[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  
  // Sort state
  const [isSortMode, setIsSortMode] = useState(false);
  const [sortResults, setSortResults] = useState<Order[]>([]);
  const [sortLoading, setSortLoading] = useState(false);
  
  // Combined mode state
  const [combinedResults, setCombinedResults] = useState<Order[]>([]);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<OrderStatus[]>([]);
  const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState<string[]>([]);
  
  // Sort state
  const [sortField, setSortField] = useState<OrderSortField | null>(null);
  const [sortOrder, setSortOrder] = useState<OrderSortOrder>('desc');
  
  // Cache ref
  const cacheRef = useRef<Map<string, CustomerOrdersResult>>(new Map());
  
  // Computed state
  const isCombinedMode = (isSearchMode && isFilterMode) || (isSearchMode && isSortMode) || (isFilterMode && isSortMode) || (isSearchMode && isFilterMode && isSortMode);
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch all customer orders (with caching)
  const fetchAllCustomerOrders = useCallback(async (): Promise<Order[]> => {
    if (!customerId || !enabled) return [];
    
    const cacheKey = `customer_orders_${customerId}`;
    
    // Check cache first
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      console.log('Using cached customer orders');
      return cached.orders;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Fetch all orders and filter by customer ID
      const result = await searchOrders('', 'All');
      const customerOrders = result.orders.filter(order => 
        order.customer.id === customerId || order.userId === customerId
      );
      
      // Sort by creation date (newest first) by default
      customerOrders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      // Cache the result
      cacheRef.current.set(cacheKey, { orders: customerOrders, total: customerOrders.length });
      
      return customerOrders;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch customer orders';
      setError(errorMessage);
      console.error('Customer orders fetch error:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [customerId, enabled]);

  // Apply search to orders
  const applySearch = useCallback((orders: Order[], query: string): Order[] => {
    if (!query.trim()) return orders;
    
    const lowerQuery = query.toLowerCase();
    return orders.filter(order => 
      order.orderNumber?.toLowerCase().includes(lowerQuery) ||
      order.id.toLowerCase().includes(lowerQuery) ||
      order.items?.some(item => 
        item.name?.toLowerCase().includes(lowerQuery)
      )
    );
  }, []);

  // Apply filters to orders
  const applyFilters = useCallback((orders: Order[]): Order[] => {
    let filtered = [...orders];
    
    // Filter by order status
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(order => selectedStatuses.includes(order.status));
    }
    
    // Filter by payment status
    if (selectedPaymentStatuses.length > 0) {
      filtered = filtered.filter(order => {
        const paymentStatus = order.payment?.status || 'pending';
        return selectedPaymentStatuses.includes(paymentStatus);
      });
    }
    
    return filtered;
  }, [selectedStatuses, selectedPaymentStatuses]);

  // Apply sorting to orders
  const applySorting = useCallback((orders: Order[]): Order[] => {
    if (!sortField) return orders;
    
    const sorted = [...orders];
    
    sorted.sort((a, b) => {
      let aVal: any;
      let bVal: any;
      
      switch (sortField) {
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        case 'total':
          aVal = a.total || 0;
          bVal = b.total || 0;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'updatedAt':
          aVal = new Date(a.updatedAt).getTime();
          bVal = new Date(b.updatedAt).getTime();
          break;
        case 'customer':
          aVal = a.customer.name || '';
          bVal = b.customer.name || '';
          break;
        case 'paymentStatus':
          aVal = a.payment?.status || 'pending';
          bVal = b.payment?.status || 'pending';
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
    
    return sorted;
  }, [sortField, sortOrder]);

  // Get paginated results
  const getPaginatedResults = useCallback((orders: Order[], page: number): Order[] => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return orders.slice(startIndex, endIndex);
  }, [pageSize]);

  // Process orders based on current filters/search/sort
  const processOrders = useCallback(async () => {
    const allOrders = await fetchAllCustomerOrders();
    if (allOrders.length === 0) {
      setOrders([]);
      setTotalCount(0);
      setAllCustomerOrders([]);
      setSummaryStats({ totalOrders: 0, totalSpent: 0, averageOrderValue: 0 });
      return;
    }
    
    setAllCustomerOrders(allOrders);
    
    // Calculate summary stats from ALL customer orders (not filtered)
    const totalSpent = allOrders.reduce((sum, order) => sum + order.total, 0);
    const averageOrderValue = allOrders.length > 0 ? totalSpent / allOrders.length : 0;
    setSummaryStats({
      totalOrders: allOrders.length,
      totalSpent,
      averageOrderValue,
    });
    
    let processedOrders = allOrders;
    
    // Apply search
    if (searchQuery.trim()) {
      setSearchLoading(true);
      processedOrders = applySearch(processedOrders, searchQuery);
      setSearchResults(processedOrders);
      setIsSearchMode(true);
      setSearchLoading(false);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
    
    // Apply filters
    if (selectedStatuses.length > 0 || selectedPaymentStatuses.length > 0) {
      setFilterLoading(true);
      processedOrders = applyFilters(processedOrders);
      setFilterResults(processedOrders);
      setIsFilterMode(true);
      setFilterLoading(false);
    } else {
      setIsFilterMode(false);
      setFilterResults([]);
    }
    
    // Apply sorting
    if (sortField) {
      setSortLoading(true);
      processedOrders = applySorting(processedOrders);
      setSortResults(processedOrders);
      setIsSortMode(true);
      setSortLoading(false);
    } else {
      setIsSortMode(false);
      setSortResults([]);
    }
    
    // Update combined results if in combined mode
    if (isCombinedMode) {
      setCombinedResults(processedOrders);
    }
    
    // Set totals and paginated results
    setTotalCount(processedOrders.length);
    const paginatedOrders = getPaginatedResults(processedOrders, currentPage);
    setOrders(paginatedOrders);
  }, [
    fetchAllCustomerOrders,
    searchQuery,
    selectedStatuses,
    selectedPaymentStatuses,
    sortField,
    sortOrder,
    currentPage,
    applySearch,
    applyFilters,
    applySorting,
    getPaginatedResults,
    isCombinedMode
  ]);

  // Debounced search handler
  const debouncedSearch = useDebouncedCallback((query: string) => {
    setCurrentPage(1); // Reset to first page on search
    processOrders();
  }, 300);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    debouncedSearch(query);
  }, [debouncedSearch]);

  const setSorting = useCallback((field: OrderSortField | null, order: OrderSortOrder) => {
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page on sort
  }, []);

  const fetchPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const refresh = useCallback(async () => {
    // Clear cache
    cacheRef.current.clear();
    setCurrentPage(1);
    await processOrders();
  }, [processOrders]);

  const reset = useCallback(() => {
    setSearchQuery('');
    setSelectedStatuses([]);
    setSelectedPaymentStatuses([]);
    setSortField(null);
    setSortOrder('desc');
    setCurrentPage(1);
    setIsSearchMode(false);
    setIsFilterMode(false);
    setIsSortMode(false);
    cacheRef.current.clear();
  }, []);

  // Effects
  useEffect(() => {
    if (enabled && customerId) {
      processOrders();
    }
  }, [enabled, customerId, processOrders]);

  // Effect for pagination
  useEffect(() => {
    if (allCustomerOrders.length > 0) {
      let processedOrders = allCustomerOrders;
      
      // Apply current filters/search/sort
      if (searchQuery.trim()) {
        processedOrders = applySearch(processedOrders, searchQuery);
      }
      
      if (selectedStatuses.length > 0 || selectedPaymentStatuses.length > 0) {
        processedOrders = applyFilters(processedOrders);
      }
      
      if (sortField) {
        processedOrders = applySorting(processedOrders);
      }
      
      const paginatedOrders = getPaginatedResults(processedOrders, currentPage);
      setOrders(paginatedOrders);
    }
  }, [currentPage, allCustomerOrders, searchQuery, selectedStatuses, selectedPaymentStatuses, sortField, sortOrder, applySearch, applyFilters, applySorting, getPaginatedResults]);

  return {
    orders,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    searchQuery,
    selectedStatuses,
    selectedPaymentStatuses,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    sortLoading,
    summaryStats,
    setSearchQuery,
    setSelectedStatuses,
    setSelectedPaymentStatuses,
    setSorting,
    handleSearch,
    fetchPage,
    refresh,
    reset,
  };
}; 